import { useId, useRef, type ReactNode, type KeyboardEvent } from 'react';
import {
  Input,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
} from '@fluentui/react-components';
import { Checkmark16Regular, SearchRegular } from '@fluentui/react-icons';
import { usePillStyles } from './pillStyles';

interface PillMenuProps<T> {
  /** Section heading shown above the search input (e.g. "Projects"). */
  sectionTitle: string;
  /**
   * Optional content rendered above the section title. Currently unused by
   * any pill, but kept as a slot for future expansions (e.g. a pinned
   * section, scope picker). A divider is appended automatically when this
   * is present. ArrowUp on the search input focuses the last menuitem in
   * here, so headers should consist of `MenuItem`/`MenuItemRadio` rows.
   */
  header?: ReactNode;

  // ─── Search ──────────────────────────────────────────────────────
  search: string;
  setSearch: (value: string) => void;
  searchPlaceholder: string;
  searchAriaLabel: string;

  // ─── List ────────────────────────────────────────────────────────
  items: readonly T[];
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  isCurrent: (item: T) => boolean;
  onSelect: (item: T) => void;

  // ─── Empty states ────────────────────────────────────────────────
  isSearching: boolean;
  /** Shown when the list is empty and the user is *not* searching. */
  emptyCopy: string;
  /** Shown when a search returns zero matches. */
  noMatchCopy: string;

  // ─── Footer "View all" (optional) ────────────────────────────────
  /** When all three of label/icon/onClick are provided, render the
   *  footer action. Omit them to drop the divider + footer entirely. */
  viewAllLabel?: string;
  viewAllIcon?: JSX.Element;
  onViewAll?: () => void;

  // ─── Inline action on the selected row (optional) ────────────────
  /** When provided, the selected row shows this text as a hyperlink-style
   *  affordance on its right edge. Clicking anywhere on that row triggers
   *  the row's normal onSelect — the label is a visual cue, not a separate
   *  interactive widget (nested widgets inside menuitems violate ARIA). */
  currentItemActionLabel?: string;

  /** When true, the current row's label is styled as a hyperlink (brand
   *  color + hover underline) even when no `currentItemActionLabel` is set.
   *  Used by `WorkflowLeafPill`: selecting the current workflow re-routes
   *  to its designer URL (a router no-op when already there, but closes
   *  the menu and keeps the link affordance truthful). */
  highlightCurrentAsLink?: boolean;
}

/**
 * Shared popover layout for the three header pill menus.
 *
 * The three pills (Workspace, App, Workflow) historically duplicated the
 * same popover skeleton — section header, search, scrollable list, divider,
 * and "View all" footer. This component centralizes that skeleton so visual
 * and behavioural changes land in one place.
 */
export function PillMenu<T>({
  sectionTitle,
  header,
  search,
  setSearch,
  searchPlaceholder,
  searchAriaLabel,
  items,
  getKey,
  getLabel,
  isCurrent,
  onSelect,
  isSearching,
  emptyCopy,
  noMatchCopy,
  viewAllLabel,
  viewAllIcon,
  onViewAll,
  currentItemActionLabel,
  highlightCurrentAsLink,
}: PillMenuProps<T>) {
  const styles = usePillStyles();
  const sectionId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // Move focus out of the search input into a menu item. Without these the
  // search input traps the keyboard user: Fluent's Menu restricts arrow-key
  // navigation to MenuItems, and Tab escapes the popover entirely.
  //
  //  ArrowDown → first menuitem in the scrollable list below the search.
  //  ArrowUp   → last menuitem in the optional header above the search
  //              (e.g. the region trigger in the workspace pill).
  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      // Search the entire MenuList (not just the scroll wrapper) so that when
      // the list is empty we still fall back to the always-present "View all"
      // footer, keeping the popover keyboard-reachable.
      const firstItem = listRef.current?.querySelector<HTMLElement>(
        '[role="menuitem"], [role="menuitemradio"]',
      );
      if (firstItem) {
        e.preventDefault();
        firstItem.focus();
      }
    } else if (e.key === 'ArrowUp') {
      const headerItems = headerRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"], [role="menuitemradio"]',
      );
      const lastItem = headerItems && headerItems[headerItems.length - 1];
      if (lastItem) {
        e.preventDefault();
        lastItem.focus();
      }
    }
  };

  return (
    <MenuPopover className={styles.menuPopover}>
      {header && (
        <>
          <div ref={headerRef}>{header}</div>
          <MenuDivider />
        </>
      )}
      <div id={sectionId} className={styles.menuItemSection}>
        {sectionTitle}
      </div>
      <div className={styles.searchContainer}>
        <Input
          size="small"
          className={styles.searchInput}
          contentBefore={<SearchRegular />}
          placeholder={searchPlaceholder}
          value={search}
          onChange={(_e, data) => setSearch(data.value)}
          onKeyDown={handleSearchKeyDown}
          aria-label={searchAriaLabel}
          autoFocus
        />
      </div>
      {items.length === 0 ? (
        // Rendered outside MenuList: ARIA menus expect only menuitem children,
        // so the empty-state message lives as a sibling status region instead.
        <div className={styles.noResults} role="status">
          {isSearching ? noMatchCopy : emptyCopy}
        </div>
      ) : null}
      {/* The ref wraps the MenuList (not just the scroll container) so the
          ArrowDown handler can fall back to the "View all" footer when the
          list is empty. Fluent's menu navigation walks descendants, so the
          extra wrapper does not affect arrow-key behaviour. */}
      <div ref={listRef}>
        <MenuList aria-labelledby={sectionId}>
          {/* Scroll wrapper sits inside the MenuList so the items can overflow
              while the "View all" footer stays pinned. */}
          <div className={styles.scrollableList}>
            {items.map((item) => {
              const current = isCurrent(item);
              const showActionHint = current && currentItemActionLabel;
              const linkifyLabel = current && (showActionHint || highlightCurrentAsLink);
              return (
                <MenuItem
                  key={getKey(item)}
                  onClick={() => onSelect(item)}
                  className={current ? styles.currentItem : undefined}
                  aria-current={current ? 'page' : undefined}
                  icon={
                    current ? (
                      <Checkmark16Regular
                        className={styles.checkmark}
                        aria-hidden="true"
                      />
                    ) : (
                      // Reserve the icon slot so labels align across rows;
                      // without this the unchecked rows shift left.
                      <span className={styles.iconPlaceholder} aria-hidden="true" />
                    )
                  }
                >
                  {showActionHint ? (
                    <span className={styles.menuItemRow}>
                      <span
                        className={`${styles.menuItemLabel} ${styles.menuItemLabelLink} otto-pill-link`}
                      >
                        {getLabel(item)}
                      </span>
                      {/* Visual affordance only. Clicking anywhere on the row
                          triggers onSelect; nesting a real interactive widget
                          inside a menuitem violates ARIA. */}
                      <span
                        className={`${styles.inlineLink} otto-pill-link`}
                        aria-hidden="true"
                      >
                        {currentItemActionLabel}
                      </span>
                    </span>
                  ) : linkifyLabel ? (
                    // No action hint, but still treat the current resource
                    // name as a hyperlink. Wrapping in menuItemRow keeps the
                    // parent-hover underline rule consistent across all pills.
                    <span className={styles.menuItemRow}>
                      <span
                        className={`${styles.menuItemLabel} ${styles.menuItemLabelLink} otto-pill-link`}
                      >
                        {getLabel(item)}
                      </span>
                    </span>
                  ) : (
                    <span className={styles.menuItemLabel}>{getLabel(item)}</span>
                  )}
                </MenuItem>
              );
            })}
          </div>
          {viewAllLabel && viewAllIcon && onViewAll && (
            <>
              {items.length > 0 && <MenuDivider />}
              <MenuItem onClick={onViewAll} icon={viewAllIcon}>
                {viewAllLabel}
              </MenuItem>
            </>
          )}
        </MenuList>
      </div>
    </MenuPopover>
  );
}
